/**
 * EP Configurator - Premium Animations
 * - 3D Hover Effect on mockup
 * - Counter Animation on stats
 * - Intersection Observer for scroll triggers
 */

(function() {
  'use strict';

  // ============================================
  // 3D HOVER EFFECT
  // ============================================
  function init3DHover() {
    const mockup = document.querySelector('.ep-configurator__mockup');
    if (!mockup) return;

    const mockupInner = mockup.querySelector('.mockup__frame');
    if (!mockupInner) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = null;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    function lerp(start, end, factor) {
      return start + (end - start) * factor;
    }

    function animate() {
      currentX = lerp(currentX, targetX, 0.1);
      currentY = lerp(currentY, targetY, 0.1);

      mockupInner.style.transform = `
        perspective(1000px)
        rotateY(${currentX}deg)
        rotateX(${currentY}deg)
        scale3d(1.02, 1.02, 1.02)
      `;

      if (Math.abs(targetX - currentX) > 0.01 || Math.abs(targetY - currentY) > 0.01) {
        rafId = requestAnimationFrame(animate);
      }
    }

    mockup.addEventListener('mousemove', function(e) {
      const rect = mockup.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      targetX = (x - 0.5) * 12; // Max 6 degrees rotation
      targetY = (y - 0.5) * -8; // Max 4 degrees rotation

      if (!rafId) {
        rafId = requestAnimationFrame(animate);
      }
    });

    mockup.addEventListener('mouseleave', function() {
      targetX = 0;
      targetY = 0;
      if (!rafId) {
        rafId = requestAnimationFrame(animate);
      }
    });
  }

  // ============================================
  // COUNTER ANIMATION
  // ============================================
  function animateCounter(element) {
    const target = parseFloat(element.dataset.target) || 0;
    const suffix = element.dataset.suffix || '';
    const prefix = element.dataset.prefix || '';
    const duration = 2000;
    const startTime = performance.now();
    const startValue = 0;

    // Easing function (ease-out cubic)
    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = startValue + (target - startValue) * easedProgress;

      // Format number
      let displayValue;
      if (target % 1 !== 0) {
        displayValue = currentValue.toFixed(1);
      } else if (target >= 100) {
        displayValue = Math.round(currentValue).toLocaleString('fr-FR');
      } else {
        displayValue = Math.round(currentValue);
      }

      element.textContent = prefix + displayValue + suffix;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  // ============================================
  // INTERSECTION OBSERVER
  // ============================================
  function initScrollAnimations() {
    const section = document.querySelector('.ep-configurator');
    if (!section) return;

    const counters = section.querySelectorAll('[data-target]');
    const features = section.querySelectorAll('.feature-card, .feature-item');
    let hasAnimated = false;

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;

          // Add visible class to section
          section.classList.add('is-visible');

          // Animate counters with stagger
          counters.forEach((counter, index) => {
            setTimeout(() => {
              animateCounter(counter);
            }, index * 150);
          });

          // Animate feature items/cards with stagger
          features.forEach((pill, index) => {
            setTimeout(() => {
              pill.classList.add('is-visible');
            }, 400 + index * 100);
          });

          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(section);
  }

  // ============================================
  // LAZY VIDEO LOAD (performance)
  // ============================================
  function initLazyVideo() {
    const section = document.querySelector('.ep-configurator');
    if (!section) return;

    const video = section.querySelector('[data-ep-video]');
    const playButton = section.querySelector('[data-ep-video-play]');
    if (!video) return;

    const source = video.querySelector('source[data-src]');
    if (!source) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let loaded = false;
    function loadVideo() {
      if (loaded) return;
      loaded = true;
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      video.load();
    }

    function playVideo() {
      loadVideo();
      if (prefersReducedMotion) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
      section.classList.add('ep-configurator--video-playing');
    }

    if (playButton) {
      playButton.addEventListener('click', playVideo);
    }

    video.addEventListener('loadeddata', () => {
      section.classList.add('ep-configurator--video-ready');
    });

    // Preload once visible (but do not auto-play).
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadVideo();
          observer.disconnect();
        });
      }, { root: null, threshold: 0.2 });

      observer.observe(section);
    }
  }

  // ============================================
  // GLOW PULSE SYNC (optional enhancement)
  // ============================================
  function initGlowEffect() {
    const glow = document.querySelector('.mockup__glow');
    if (!glow) return;

    // The glow animation is handled by CSS
    // This function can be used for additional JS-based effects
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    init3DHover();
    initScrollAnimations();
    initLazyVideo();
    initGlowEffect();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
